import { useTranslation } from "react-i18next";

import { PaymentStatusButton } from "../../../../components/PaymentStatusButton";
import { putCollectionPointPaymentThunk } from "../../../../reducers/collectionPointReducer";
import { getCollectionPointIsUpdating } from "../../../../selectors/collectionPoint";
import { IBillingPayment } from "../../../../types/billings";
import { ICollectionPointBilling } from "../../../../types/collectionPoint";
import { formatCurrency } from "../../../../utils/currency";
import { getStringFromISOString } from "../../../../utils/dateHandler";
import { BillingCardContainer } from "../BillingCardContainer";
import { BillingLabelValue, BillingLabelValueSize } from "../BillingLabelValue";

import styles from "./BillingPaymentAnnualCard.module.scss";

interface BillingPaymentAnnualCardProps {
  billing: ICollectionPointBilling;
  payment?: IBillingPayment;
  dueDate?: string;
  isDisabled?: boolean;
  isTotal?: boolean;
  year: number;
}

export const BillingPaymentAnnualCard = ({
  payment,
  billing,
  dueDate,
  isDisabled = false,
  isTotal = false,
  year,
}: BillingPaymentAnnualCardProps) => {
  const { t } = useTranslation();

  const overPayment = (billing.netAmount ?? 0) - (billing?.amount ?? 0);
  const lastYear = year - 1;

  return (
    <BillingCardContainer className={styles.container}>
      <div className={styles.leftContainer}>
        <BillingLabelValue
          label={t(`collectionPoint.billingPaymentAnnualCard.depositAmount${isTotal ? "Total" : ""}`)}
          value={formatCurrency(billing.netAmount ?? 0)}
          valueSize={BillingLabelValueSize.Large}
        />
        {dueDate && (
          <BillingLabelValue
            label={t("collectionPoint.billingPaymentAnnualCard.dueDate")}
            value={getStringFromISOString(dueDate)}
          />
        )}
        <BillingLabelValue
          label={t(`collectionPoint.billingPaymentAnnualCard.calculatedDeposit${isTotal ? "Total" : ""}`, { year })}
          value={formatCurrency(billing.amount ?? 0)}
        />
        {overPayment < 0 && (
          <BillingLabelValue
            label={t("collectionPoint.billingPaymentAnnualCard.overpayment", { year: lastYear })}
            value={formatCurrency(-overPayment)}
            valueColor="var(--green)"
          />
        )}
        {overPayment > 0 && (
          <BillingLabelValue
            label={t("collectionPoint.billingPaymentAnnualCard.underpayment", { year: lastYear })}
            value={formatCurrency(overPayment)}
            valueColor="var(--red-600)"
          />
        )}
      </div>
      {payment && (
        <div className={styles.rightContainer}>
          <p>{t("collectionPoint.billingPaymentAnnualCard.paid")}</p>
          <PaymentStatusButton
            thunk={putCollectionPointPaymentThunk}
            isPaid={!!payment.paymentDate}
            paidAt={payment.paymentDate}
            payload={{
              period: payment.period,
              isPaid: !payment.paymentDate,
            }}
            translationKey="collectionPoint.billingPaymentChangeModal"
            isUpdating={getCollectionPointIsUpdating}
            isDisabled={isDisabled}
          />
        </div>
      )}
    </BillingCardContainer>
  );
};
